"use client";

// useAutoRetry — the quiet retry an error screen does on its own.
//
// Retry after 1.5s, then 4s, then 10s (PAGE_BACKOFF_MS), then stop and let the
// button speak. The count is kept against the error's digest in sessionStorage,
// so a deterministic bug can never loop: a remount of the error screen reads the
// same count back instead of starting the schedule over. A retry waits for the
// tab to be visible and the network to be there; trying into a hidden tab or a
// dead connection only spends an attempt.
//
// It carries no styling, so global-error.tsx (which has none of the app's css)
// can use it too.

import { useCallback, useEffect, useRef, useState } from "react";
import { PAGE_BACKOFF_MS, errorKey, nextRetryDelay, recordRetry } from "../../lib/os/resilience";

/** how long one retry may stay in flight before the next step is taken anyway */
const WATCHDOG_MS = 12_000;

function sessionStore(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function useAutoRetry({
  where,
  error,
  retry,
  schedule = PAGE_BACKOFF_MS,
  enabled = true,
}: {
  where: string;
  error: { digest?: string; message?: string };
  retry: () => void;
  schedule?: readonly number[];
  enabled?: boolean;
}) {
  const [phase, setPhase] = useState<"waiting" | "gave-up">("waiting");
  const [busy, setBusy] = useState(false);
  // bumped by the watchdog below, so a retry that neither succeeds nor fails
  // (a server that hangs) cannot leave "reconnecting" on screen for ever
  const [round, setRound] = useState(0);
  const retryRef = useRef(retry);
  useEffect(() => {
    retryRef.current = retry;
  });
  const key = errorKey(where, error);

  useEffect(() => {
    if (!enabled) return;
    const store = sessionStore();
    const delay = nextRetryDelay(store, where, error, schedule, Date.now());
    if (delay == null) {
      setPhase("gave-up");
      return;
    }
    setPhase("waiting");
    let timer: ReturnType<typeof setTimeout> | null = null;
    let watchdog: ReturnType<typeof setTimeout> | null = null;
    let due = false;
    const ready = () => document.visibilityState !== "hidden" && navigator.onLine !== false;
    const fire = () => {
      due = false;
      recordRetry(store, where, error, Date.now());
      watchdog = setTimeout(() => setRound((n) => n + 1), WATCHDOG_MS);
      retryRef.current();
    };
    const wake = () => {
      if (due && ready()) fire();
    };
    timer = setTimeout(() => {
      timer = null;
      due = true;
      wake();
    }, delay);
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("online", wake);
    return () => {
      if (timer) clearTimeout(timer);
      if (watchdog) clearTimeout(watchdog);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("online", wake);
    };
    // `error` (the object, not just its key) is a dependency on purpose: a retry
    // that fails again may leave THIS component mounted with the same digest, and
    // the fresh error object is the only sign that it failed, so the next wait
    // (or the give-up) starts then, never while the last try is still in flight.
    // schedule is a module constant.
  }, [key, enabled, error, round]); // eslint-disable-line react-hooks/exhaustive-deps

  const retryNow = useCallback(() => {
    setBusy(true);
    // the press answers at once; the spinner rests a beat so it never flickers
    setTimeout(() => setBusy(false), 700);
    retryRef.current();
  }, []);

  return { phase, busy, retryNow };
}

